import { Box, Divider, Menu, MenuItem, Typography } from '@mui/material'
import { BookUser, ChevronDown, FileText, LogOut, Sprout, Upload } from 'lucide-react'
import { useLocation } from '../lib/router'
import { useGoHome } from '../lib/home'
import { initialsOf, usePersonLabel } from '../lib/people'
import { useNavigationGuard } from './NavigationGuard'
import { useEffect, useState, type ReactNode } from 'react'
import { useIsFetching } from '@tanstack/react-query'
import { tokens } from '../theme'
import { useCapabilities, useSession } from '../api/hooks'
import { useHost } from '../host'
import logoMark from '../assets/logo-mark.svg'
import { useEdition, type EditionSection } from '../edition'

/**
 * The rail, in two groups.
 *
 * `needs` hides an entry from anyone without that privilege, and a group
 * with nothing left in it disappears entirely — so an ordinary analyst sees
 * no admin section at all rather than a section that answers 403. The API
 * refuses these routes either way; this is about not showing somebody a
 * door they cannot open.
 */
export interface Can {
  key: boolean; anyKey: boolean; org: boolean; platform: boolean; kind: 'staff' | 'supplier' | 'farmer'
  features: { eudr: boolean; sourcing: boolean }
}

export type NavItem = {
  to: string
  label: string
  Icon: typeof Upload
  /** Omitted where anyone signed in may go. */
  show?: (can: Can) => boolean
}

/** The admin group and any further sections are the edition's: empty where it has none. */
const nav = (adminNav: NavItem[], sections: EditionSection[] = []): { title: string; items: NavItem[]; feature?: keyof Can['features'] }[] => [
  {
    title: 'Supply chain',
    // The EUDR plot checks: hidden for an organisation that only collects supplier data.
    feature: 'eudr',
    items: [
      // Not "Polygon checks" — that is the product, and this screen's job
      // is narrower: choose or create a region and get polygons into it.
      // Not "Create region" either, because it adds to an existing one just
      // as often as it makes a new one.
      // A supplier's or farmer's login starts from their own plots.
      { to: '/farms', label: 'My farms', Icon: Sprout, show: (c) => c.kind === 'supplier' },
      { to: '/fields', label: 'My fields', Icon: Sprout, show: (c) => c.kind === 'farmer' },
      { to: '/checks', label: 'Add polygons', Icon: Upload },
      { to: '/regions', label: 'Regions', Icon: FileText },
      // The directory is the organisation's to keep.
      { to: '/directory', label: 'Directory', Icon: BookUser, show: (c) => c.kind === 'staff' && !c.anyKey },
    ],
  },
  ...sections.map((s) => ({ title: s.title, items: s.nav, feature: s.feature })),
  { title: 'Admin', items: adminNav },
]

/** Left rail only — the design system has no top navigation inside the app. */
function LeftRail() {
  const { pathname } = useLocation()
  const { go } = useNavigationGuard()
  const can = useCapabilities()
  // No key of any scope holds a region or a plot, so an entry with no rule
  // of its own is for people. Keying this on the partner scope alone left a
  // platform key looking at a Supply chain group it could not use.
  const allowed = (show?: (c: Can) => boolean) => (show ? show(can) : !can.anyKey)
  const { adminNav, sections } = useEdition()
  const groups = nav(adminNav, sections)
    .filter((g) => !g.feature || can.features[g.feature])
    .map((g) => ({ ...g, items: g.items.filter((i) => allowed(i.show)) }))
    .filter((g) => g.items.length > 0)
  return (
    <Box
      component="nav"
      aria-label="Sections"
      sx={{
        width: 200, flex: 'none', backgroundColor: 'background.paper',
        borderRight: `1px solid ${tokens.color.ink200}`,
        py: 5, px: 3, display: 'flex', flexDirection: 'column', gap: 0.5,
      }}
    >
      {groups.map((group, groupIndex) => (
      <Box key={group.title} sx={{ display: 'contents' }}>
      <Typography
        variant="overline"
        sx={{ color: 'text.disabled', px: 2.5, pb: 2.5, pt: groupIndex > 0 ? 5 : 0 }}
      >
        {group.title}
      </Typography>
      {group.items.map(({ to, label, Icon }) => {
        const active = pathname === to || pathname.startsWith(`${to}/`)
        return (
          <Box
            key={to}
            component="button"
            type="button"
            onClick={() => go(to)}
            sx={{
              border: 0, width: '100%', textAlign: 'left', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 2.5, px: 2.5, py: 2,
              borderRadius: `${tokens.radius.md}px`, textDecoration: 'none',
              font: `${active ? 500 : 400} 14px ${tokens.font.sans}`,
              color: active ? 'text.primary' : 'text.secondary',
              backgroundColor: active ? tokens.color.ink100 : 'transparent',
              transition: `background ${tokens.motion.dur.fast}ms ${tokens.motion.easeStd}`,
              '&:hover': { backgroundColor: tokens.color.ink100 },
            }}
          >
            <Icon size={18} strokeWidth={1.5} />
            {label}
          </Box>
        )
      })}
      </Box>
      ))}
    </Box>
  )
}

export function TopBar({ crumbs, actions }: { crumbs: ReactNode[]; actions?: ReactNode }) {
  const goHome = useGoHome()
  return (
    <Box
      component="header"
      sx={{
        height: 56, flex: 'none', display: 'flex', alignItems: 'center', gap: 2.5, px: 5,
        backgroundColor: 'background.paper', borderBottom: `1px solid ${tokens.color.ink200}`,
      }}
    >
      {/* The mark is the way home, and it asks first if work would be lost. */}
      <Box
        component="button"
        type="button"
        aria-label="Home"
        title="Home"
        onClick={() => goHome()}
        sx={{
          width: 28, height: 28, p: 0, border: 0, flex: 'none', cursor: 'pointer',
          display: 'block', backgroundColor: 'transparent',
          transition: `opacity ${tokens.motion.dur.fast}ms ${tokens.motion.easeStd}`,
          '&:hover': { opacity: 0.75 },
        }}
      >
        <Box component="img" src={logoMark} alt="" sx={{ width: '100%', height: '100%', display: 'block' }} />
      </Box>
      {crumbs.map((crumb, i) => (
        <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 2.5, minWidth: 0 }}>
          {i > 0 && <Box component="span" sx={{ color: 'text.disabled' }}>/</Box>}
          {crumb}
        </Box>
      ))}
      <Box sx={{ flex: 1 }} />
      {actions}
      <AccountMenu />
    </Box>
  )
}

/**
 * Who you are, and the way out.
 *
 * Signing out matters more than it used to: an access token lives in this
 * browser until something removes it, and without this there is nothing to
 * remove it with.
 */
function AccountMenu() {
  const { data: user } = useSession()
  // Signing out is the application around us's to do: it holds the token or
  // the session. No entry where it did not say how.
  const { onSignOut } = useHost()
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  // The host's own name for this user, when it gives one.
  const name = usePersonLabel(user?.id, user?.full_name ?? '')
  const initials = name && name !== user?.full_name ? initialsOf(name) : user?.initials

  const how = user?.auth_method === 'auth0' ? 'Signed in with Auth0'
    : user?.auth_method === 'user-token' ? 'Using an access token'
    : user?.auth_method === 'fallback' ? 'No sign-in on this deployment'
    : null

  return (
    <>
      {/*
        The avatar alone read as decoration — nobody found the way out
        because nothing said there was one. The chevron and the hover state
        are the whole of the fix: it has to look like a control.
      */}
      <Box
        component="button"
        type="button"
        aria-label={user ? `${name} — account menu` : 'Account menu'}
        aria-haspopup="menu"
        aria-expanded={Boolean(anchor)}
        title={user ? `${name} · ${user.organisation_name}` : undefined}
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={{
          display: 'flex', alignItems: 'center', gap: 1, flex: 'none',
          ml: 1.5, pl: 0.5, pr: 1, py: 0.5, border: 0, cursor: 'pointer',
          borderRadius: `${tokens.radius.md}px`, backgroundColor: 'transparent',
          color: 'text.secondary',
          transition: `background ${tokens.motion.dur.fast}ms ${tokens.motion.easeStd}`,
          '&:hover': { backgroundColor: tokens.color.ink100 },
          '&:focus-visible': { outline: `2px solid ${tokens.color.brandTeal}`, outlineOffset: 2 },
        }}
      >
        <Box
          sx={{
            width: 30, height: 30, borderRadius: '50%', flex: 'none',
            backgroundColor: tokens.color.brandDeep, color: tokens.color.ink0,
            display: 'grid', placeItems: 'center', font: `500 12px ${tokens.font.sans}`,
          }}
        >
          {initials ?? '··'}
        </Box>
        <ChevronDown
          size={14}
          strokeWidth={1.5}
          style={{
            transition: `transform ${tokens.motion.dur.fast}ms ${tokens.motion.easeStd}`,
            transform: anchor ? 'rotate(180deg)' : 'none',
          }}
        />
      </Box>
      <Menu
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ px: 4, py: 2, maxWidth: 260 }}>
          <Typography variant="body2" noWrap>{name}</Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }} noWrap>
            {user?.organisation_name}
          </Typography>
          {how && (
            <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', mt: 1 }}>
              {how}
            </Typography>
          )}
        </Box>
        {onSignOut && <Divider />}
        {onSignOut && (
          <MenuItem
            onClick={() => {
              setAnchor(null)
              onSignOut()
            }}
            sx={{ gap: 2.5 }}
          >
            <LogOut size={16} strokeWidth={1.5} />
            Sign out
          </MenuItem>
        )}
      </Menu>
    </>
  )
}

/**
 * The first crumb on every screen, and the way home (see `useGoHome`). It
 * used to lead nowhere, or back to Add polygons (AG-87).
 */
export function HomeCrumb() {
  const goHome = useGoHome()
  return <Crumb onClick={() => goHome()}>agribot</Crumb>
}

export function Crumb({
  children, muted, mono, to, onClick,
}: { children: ReactNode; muted?: boolean; mono?: boolean; to?: string; onClick?: () => void }) {
  const { go } = useNavigationGuard()
  const action = onClick ?? (to ? () => go(to) : undefined)
  return (
    <Box
      component={action ? 'button' : 'span'}
      type={action ? 'button' : undefined}
      onClick={action}
      sx={{
        ...(action
          ? {
              border: 0, p: 0, background: 'none', cursor: 'pointer',
              '&:hover': { color: tokens.color.brandTeal, textDecoration: 'underline' },
              textUnderlineOffset: 2,
            }
          : {}),
        font: mono
          ? `400 13px ${tokens.font.mono}`
          : `${muted ? 400 : 500} 14px ${tokens.font.sans}`,
        color: muted ? 'text.secondary' : 'text.primary',
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 320,
      }}
    >
      {children}
    </Box>
  )
}

/**
 * Whether anything is being fetched, anywhere.
 *
 * Screens that already have data refetch in place — a filter changes, a run
 * finishes, a list is invalidated after an edit — and until now nothing said
 * so: the figures simply changed a moment later, which reads as the screen
 * having been wrong before. One line under the top bar answers it for every
 * query at once, so no screen has to remember to.
 *
 * It waits 300 ms before appearing. Most fetches here finish well inside
 * that, and a bar that flickers on every keystroke is worse than no bar.
 */
function FetchingBar() {
  const fetching = useIsFetching()
  const [show, setShow] = useState(false)

  useEffect(() => {
    if (!fetching) {
      setShow(false)
      return
    }
    const timer = setTimeout(() => setShow(true), 300)
    return () => clearTimeout(timer)
  }, [fetching])

  return (
    <Box
      // Sits on the top bar's own border rather than above it: a 2 px band
      // that took layout space would nudge every screen down and back up.
      aria-hidden={!show}
      data-testid="fetching-bar"
      data-fetching={show ? 'true' : 'false'}
      role={show ? 'progressbar' : undefined}
      aria-label={show ? 'Loading data' : undefined}
      sx={{
        position: 'absolute', left: 0, right: 0, bottom: -1, height: 2,
        overflow: 'hidden', pointerEvents: 'none',
        opacity: show ? 1 : 0,
        transition: `opacity ${tokens.motion.dur.fast}ms ${tokens.motion.easeStd}`,
      }}
    >
      <Box
        sx={{
          width: '100%', height: '100%', transformOrigin: 'left center',
          backgroundColor: tokens.color.brandTeal,
          animation: show ? 'pc-sweep 1.1s ease-in-out infinite' : 'none',
        }}
      />
    </Box>
  )
}

/**
 * The application frame: 56 px top bar, left rail, content.
 * `rail={false}` for the map-first screens, which use the full width.
 */
export function AppShell({
  crumbs, actions, children, rail = true,
}: { crumbs: ReactNode[]; actions?: ReactNode; children: ReactNode; rail?: boolean }) {
  return (
    // Fills the viewport standalone, and its container when embedded: the
    // host sets --pc-shell-height on the wrapper and sizes the element itself.
    <Box sx={{
      display: 'flex', flexDirection: 'column',
      height: 'var(--pc-shell-height, 100vh)',
      backgroundColor: 'background.default',
    }}>
      <Box sx={{ position: 'relative', flex: 'none', zIndex: 2 }}>
        <TopBar crumbs={crumbs} actions={actions} />
        <FetchingBar />
      </Box>
      <Box sx={{ flex: 1, display: 'flex', minHeight: 0 }}>
        {rail && <LeftRail />}
        <Box component="main" sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          {children}
        </Box>
      </Box>
    </Box>
  )
}
